import { v4 as uuid } from 'uuid';

const SKIP_GROUPS = [
	'form-group-submit',
	'form-group-google_recaptcha_v2',
	'form-group-google_recaptcha_v3',
];

/**
 * Transforms CMS-generated .form-group markup into Bootstrap 5 fields.
 * All DOM walking, field-type detection and node replacement lives here;
 * the per-type markup is delegated to the injected `layout`.
 */
class Form {
	constructor(scope, layout) {
		this.scope = scope;
		this.layout = layout;

		this.forms = document.querySelectorAll(scope);
		if (this.forms.length) this.init();
	}

	init() {
		this.forms.forEach(form => {
			form.dataset.uniqueId = uuid();

			form.querySelectorAll('.form-group').forEach(group => {
				if (SKIP_GROUPS.some(cls => group.classList.contains(cls))) return;
				this.transformGroup(form, group);
			});
		});
	}

	transformGroup(form, group) {
		if (group.querySelectorAll('input + input').length) return this.buildMultiColumn(form, group);
		if (group.classList.contains('form-group-radio')) return this.buildOptionGroup(group, 'radio', 4);
		if (group.classList.contains('form-group-checkbox')) return this.buildOptionGroup(group, 'checkbox', 2);
		if (group.querySelector('[type="file"]')) return this.buildFile(form, group);
		if (group.querySelector('[type="date"]')) return this.buildDate(form, group);
		if (group.querySelector('textarea')) return this.buildTextarea(form, group);
		if (group.querySelector('select')) return this.buildSelect(form, group);
		if (group.querySelector('input')) return this.buildInput(form, group);
	}

	// --- shared helpers -----------------------------------------------------

	fieldId(form, name) {
		return `${form.dataset.uniqueId}-${name}`;
	}

	stripRequired(text) {
		return text ? text.replace(/\s*\*\s*$/, '') : '';
	}

	labelText(group, input = null) {
		let labelAbove = group.querySelector('.label-above');
		if (labelAbove) return this.stripRequired(labelAbove.innerHTML);

		if (input?.placeholder) return this.stripRequired(input.placeholder);

		if (input?.tagName === 'SELECT') {
			let emptyOpt = input.querySelector('option[value=""]');
			if (emptyOpt) return this.stripRequired(emptyOpt.textContent);
		}

		let firstLabel = group.querySelector(':scope > label');
		if (firstLabel) return this.stripRequired(firstLabel.textContent);

		return '';
	}

	replaceGroup(group, newEl) {
		group.after(newEl);
		group.remove();
	}

	/** Copy the CMS's form-group-* modifier classes onto a rebuilt element. */
	carryClasses(group, target) {
		[...group.classList]
			.filter(c => c.startsWith('form-group-'))
			.forEach(c => target.classList.add(c));
		return target;
	}

	/** Wrap one control via the layout, drop it into a fresh .form-group, swap it in. */
	renderField(group, { id, label, control, canFloat = true }) {
		let wrapper = document.createElement('div');
		wrapper.className = 'form-group';
		this.carryClasses(group, wrapper);
		wrapper.innerHTML = this.layout.wrapField({ id, label, control, canFloat });
		this.replaceGroup(group, wrapper);
	}

	// --- field builders -----------------------------------------------------

	buildInput(form, group) {
		let input = group.querySelector('[type="text"], [type="email"], [type="number"], [type="password"]');
		if (!input) return;

		let id = this.fieldId(form, input.name);
		let label = this.labelText(group, input);
		let placeholder = this.layout.placeholder(label);
		let autocomplete = { password: 'current-password', email: 'email' }[input.type] || '';

		let control = `<input type="${input.type}" name="${input.name}" class="form-control" id="${id}"`
			+ (placeholder ? ` placeholder="${placeholder}"` : '')
			+ (autocomplete ? ` autocomplete="${autocomplete}"` : '')
			+ (input.required ? ' required' : '')
			+ '>';

		this.renderField(group, { id, label, control });
	}

	buildTextarea(form, group) {
		let input = group.querySelector('textarea');
		let id = this.fieldId(form, input.name);
		let label = this.labelText(group, input);

		let override = form.closest('[data-textarea-placeholder]')?.dataset.textareaPlaceholder || '';
		let placeholder = this.layout.placeholder(label, override);

		let control = `<textarea class="form-control" id="${id}" name="${input.name}"`
			+ (placeholder ? ` placeholder="${placeholder}"` : '')
			+ (input.required ? ' required' : '')
			+ '></textarea>';

		this.renderField(group, { id, label: this.layout.labelFor(label, override), control });
	}

	buildSelect(form, group) {
		let input = group.querySelector('select');
		if (!input) return;

		let id = this.fieldId(form, input.name);
		let label = this.labelText(group, input);

		let options = [...input.querySelectorAll('option')].map(opt => {
			if (opt.value !== '') return opt.outerHTML;
			// Drop the empty prompt option when a real label is shown elsewhere.
			return this.layout.showsLabel ? '' : `<option value="" selected disabled>${opt.textContent}</option>`;
		}).join('');

		let control = `<select class="field-type-selectlist form-select" id="${id}" name="${input.name}" aria-label="${label}"`
			+ (input.required ? ' required' : '')
			+ `>${options}</select>`;

		this.renderField(group, { id, label, control });
	}

	buildDate(form, group) {
		let input = group.querySelector('[type="date"]');
		if (!input) return;

		let id = this.fieldId(form, input.name);
		let control = `<input type="date" name="${input.name}" class="form-control" id="${id}"${input.required ? ' required' : ''}>`;

		this.renderField(group, { id, label: this.labelText(group, input), control, canFloat: false });
	}

	buildFile(form, group) {
		let input = group.querySelector('[type="file"]');
		if (!input) return;

		let id = this.fieldId(form, input.name);
		let control = `<input type="file" name="${input.name}" class="form-control" id="${id}"${input.required ? ' required' : ''}>`;

		this.renderField(group, { id, label: this.labelText(group, input), control, canFloat: false });
	}

	buildMultiColumn(form, group) {
		let inputs = [...group.querySelectorAll('input')];
		let colClass = inputs.length === 2 ? 'col-md-6' : 'col-md-4';

		let fields = inputs.map(input => {
			let id = this.fieldId(form, input.name);
			let label = input.placeholder || input.name;
			let placeholder = this.layout.placeholder(label);

			let control = `<input type="${input.type}" name="${input.name}" class="form-control" id="${id}"`
				+ (placeholder ? ` placeholder="${placeholder}"` : '')
				+ (input.required ? ' required' : '')
				+ '>';

			return this.layout.wrapColumn({ id, label, control, colClass });
		}).join('');

		let row = document.createElement('div');
		row.className = 'form-group row g-3';
		this.carryClasses(group, row);
		row.style.rowGap = '1rem';
		row.innerHTML = fields;
		this.replaceGroup(group, row);
	}

	buildOptionGroup(group, type, inlineMax) {
		let label = this.labelText(group);
		let inputs = [...group.querySelectorAll(`[type="${type}"]`)];
		let inline = inputs.length <= inlineMax ? ' form-check-inline' : '';

		let checks = inputs.map(input => {
			let inputLabel = group.querySelector(`label[for="${input.id}"]`)?.textContent || '';
			return `
				<div class="form-check${inline}">
					<input class="form-check-input" type="${type}" name="${input.name}" id="${input.id}" value="${input.value}"${input.required ? ' required' : ''}>
					<label class="form-check-label" for="${input.id}">${inputLabel}</label>
				</div>
			`;
		}).join('');

		let fieldset = document.createElement('fieldset');
		fieldset.className = 'form-group';
		this.carryClasses(group, fieldset);
		fieldset.innerHTML = `<legend class="${this.layout.labelClass}">${label}</legend>${checks}`;
		this.replaceGroup(group, fieldset);
	}
}

export default Form;
